/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import { useState } from 'react';
import Image from 'next/image';
import { useLanguage } from '@/providers/LanguageProvider';
import Edit from 'public/shared-icons/Edit.svg';
import Trash from 'public/shared-icons/Trash.svg';
import { Component } from 'types/api-data';

import { useComponents } from '@/hooks/useComponents';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import AutoDiscoveredBadge from '@/components/common/auto-discovered-badge';
import { ConfigureComponentCard } from '@/components/components-page/components-config-card';
import { DeleteDialog } from '@/components/detail-page/delete-dialog';
import { Icons } from '@/components/icons';
import EvalStatusPopover from '@/components/nav/eval-status-popover';

import WorkflowApplication from './workflow-application';
import WorkflowFramework from './workflow-framework';
import WorkflowRepository from './workflow-repository';
import WorkflowService from './workflow-service';

const WorkflowHeadSection = ({ workflowName }: { workflowName: string }) => {
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const { messages } = useLanguage();
  const general = messages?.General;

  const {
    state: { components },
    componentsActions,
  } = useComponents();

  const findComponent = components?.find(
    (comp: Component) => comp?.component_name === workflowName
  );

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="mb-3 flex items-center gap-2">
          <h1 className="text-h1 text-body-primary">
            {findComponent?.display_name}
          </h1>
          {findComponent?.auto_discovered && <AutoDiscoveredBadge />}
          <EvalStatusPopover workflowName={workflowName} />
        </div>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            {Icons.Ellipses('border border-white text-white')}
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="min-w-32 rounded-sm bg-white text-[#323130]"
          >
            <DropdownMenuItem
              className="flex cursor-pointer items-center gap-2 hover:bg-[#EDEBE9]"
              onClick={() => setOpenEditDialog(true)}
            >
              <Image src={Edit} alt="edit icon" className="h-4 w-4" />
              <span>{general?.edit}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex cursor-pointer items-center gap-2 hover:bg-[#EDEBE9]"
              onClick={() => setOpenDeleteDialog(true)}
            >
              <Image
                src={Trash}
                alt="delete icon"
                className="h-4 w-4 stroke-2"
              />
              <span>{general?.delete}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {openDeleteDialog && (
          <DeleteDialog
            open={openDeleteDialog}
            setOpen={setOpenDeleteDialog}
            title={findComponent?.display_name}
            name={workflowName}
            isWorkflow
            refreshComponents={componentsActions.fetchComponents}
          />
        )}
        {openEditDialog && (
          <ConfigureComponentCard
            component={findComponent}
            workflowName={workflowName}
            openDialog={openEditDialog}
            setOpenDialog={setOpenEditDialog}
            isEdit
            isNew
          />
        )}
      </div>
      <div className="mb-[28px] flex text-body-secondary">
        <WorkflowService
          workflowName={workflowName}
          component={findComponent}
        />
        <WorkflowApplication
          workflowName={workflowName}
          component={findComponent}
        />
        <WorkflowRepository component={findComponent} />
        <WorkflowFramework workflowName={workflowName} />
      </div>
    </>
  );
};

export default WorkflowHeadSection;
